import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

type ConfirmOptions = {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
};

type ConfirmContextValue = {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
};

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export function useConfirm(): ConfirmContextValue {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{
    options: ConfirmOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  const queueRef = useRef<Array<{ options: ConfirmOptions; resolve: (v: boolean) => void }>>([]);
  const resolvingRef = useRef(false);

  const processNext = useCallback(() => {
    if (queueRef.current.length > 0) {
      const next = queueRef.current.shift();
      if (next) {
        setState(next);
      }
    }
  }, []);

  const confirm = useCallback(
    (options: ConfirmOptions): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        if (state) {
          queueRef.current.push({ options, resolve });
        } else {
          setState({ options, resolve });
        }
      });
    },
    [state],
  );

  const handleResolve = useCallback(
    (value: boolean) => {
      resolvingRef.current = true;
      state?.resolve(value);
      setState(null);
      requestAnimationFrame(() => {
        resolvingRef.current = false;
        processNext();
      });
    },
    [state, processNext],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && state && !resolvingRef.current) {
        handleResolve(false);
      }
    },
    [handleResolve, state],
  );

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <AlertDialog open={state !== null} onOpenChange={handleOpenChange}>
        {state && (
          <AlertDialogContent size="sm">
            <AlertDialogHeader className="place-items-start text-left">
              <AlertDialogTitle>{state.options.title}</AlertDialogTitle>
              <AlertDialogDescription>{state.options.message}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => handleResolve(false)}>
                {state.options.cancelLabel ?? 'Avbryt'}
              </AlertDialogCancel>
              <AlertDialogAction
                variant={state.options.destructive ? 'destructive' : 'default'}
                onClick={() => handleResolve(true)}
              >
                {state.options.confirmLabel ?? 'Bekräfta'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}
